import React, { useMemo, useCallback, useContext, useRef } from "react";
import { useFormikContext } from "formik";
import styled from "styled-components";
import { Flex, Box } from "reflexbox";
import Field from "../../UI/forms/Field";
import Checkbox from "../../UI/forms/Checkbox";
import { Heading } from "../../UI/Text";
import _ from "lodash";
import ConfiguratorContext from "../../../context/configurator/ConfiguratorContext";
import { updateQuote } from "../../../requests/quoteManagementRequests";
import { createAddress } from "../../../requests/addressManagementRequests";
import { useParams } from "react-router-dom";

const AddressWrapper = styled(Flex)`
	> div {
		margin-top: 0;
	}
`;

const AddressForm = ({
	isLoading,
	disableForm,
	onSubmit,
	resetForm,
	formToShow,
	setFormToShow,
	setEditingId,
	editingId,
	setRefetch,
}) => {
	const { values, setFieldValue } = useFormikContext();
	const { quoteId } = useParams();

	const { quote, setQuote } = useContext(ConfiguratorContext);
	const isPlane = useMemo(() => quote?.isPlane, [quote]);

	const { shippingAddresses } = quote || {};

	const onAutofillShipping = useCallback(
		async (_addressToFillId) => {
			let addressToFillId = _addressToFillId;

			const address = _.find(
				shippingAddresses,
				({ id }) => +id === +addressToFillId
			);

			const {
				Address = "",
				Address2 = "",
				City = "",
				State = "",
				Zip = "",
				County = "",
				Country = "",
				id,
				Name = "",
			} = address || {};
			setQuote({
				...quote,
				shippingAddressId: +addressToFillId,
				shippingAddress: {
					Address,
					County,
					Country,
					Address2,
					City,
					State,
					Zip,
					id,
					Name,
				},
			});
			if (!addressToFillId || addressToFillId === "New") {
				const { id: newId, ...rest } = await createAddress({
					input: {
						Address: "",
						Address2: "",
						City: "",
						State: "",
						Country: "",
						County: "",
						Zip: "",
						AddressType: 2,
						Name: "",
						QuoteId: +quoteId,
					},
				});
				const newAddresses = [...quote?.shippingAddresses];
				newAddresses.push({ id: newId, ...rest });
				setQuote({
					...quote,
					shippingAddressId: newId,
					shippingAddress: { id: newId, ...rest },
					shippingAddresses: newAddresses,
				});
				await updateQuote({
					id: +quoteId,
					input: { shippingAddressId: newId },
				});
				return;
			}

			await updateQuote({
				id: +quoteId,
				input: { shippingAddressId: +addressToFillId },
			});
		},
		[shippingAddresses, setQuote, quote, quoteId]
	);

	const shippingStateCodes = useMemo(
		() =>
			_.filter(
				quote?.stateCodes || [],
				({ country }) => country === values?.shipping?.Country
			),
		[quote, values.shipping]
	);
	const billingStateCodes = useMemo(
		() =>
			_.filter(
				quote?.stateCodes || [],
				({ country }) => country === values?.billing?.Country
			),
		[quote, values.billing]
	);

	const shippingStateOptions = useMemo(
		() =>
			shippingStateCodes.map(({ state, description, country }) => (
				<option value={state} key={`${country}_${state}`}>
					{description}
				</option>
			)),
		[shippingStateCodes]
	);
	const billingStateOptions = useMemo(
		() =>
			billingStateCodes.map(({ state, description, country }) => (
				<option value={state} key={`${country}_${state}`}>
					{description}
				</option>
			)),
		[billingStateCodes]
	);
	const countryOptions = useMemo(
		() =>
			quote?.countryCodes?.map(({ country, iso_country_code }) => (
				<option value={country} key={iso_country_code}>
					{country}
				</option>
			)),
		[quote]
	); 
 
	const shippingAddressIds = useMemo(() => {
		if (!shippingAddresses) return [];
		return shippingAddresses.map(({ id }) => id);
	}, [shippingAddresses]);

	const shippingOptions = useMemo(() => {
		if (!_.isArray(shippingAddresses)) return [];

		const shippingAddressesSorted = _.orderBy(shippingAddresses, ["Name"]);
		return shippingAddressesSorted.map((shipAddress) => {
			const { Address, Address2, City, State, Zip, Name } = shipAddress;

			return {
				value: shipAddress.id,
				label: ` ${Name || ""} ${Address || ""}${
					Address2 ? " " + Address2 : ""
				}, ${City || ""} ${State || ""}, ${Zip || ""}`,
			};
		});
	}, [shippingAddresses]);

	const onBlur = useCallback(
		async (e) => {
			await onSubmit({ [e.target.name]: e.target.value });
			setRefetch(null);
		},
		[onSubmit, setRefetch]
	);

	const fieldProps = useMemo(
		() => ({
			isLoading,
			onBlur,
			disabled: true,
			// disabled:
			// 	+quote?.shippingAddress?.QuoteId !== +quoteId &&
			// 	shippingAddressIds.includes(quote?.shippingAddressId),
		}),
		[isLoading, onBlur]
	);

	const ref = useRef(null);

	const onOptionClick = useCallback(
		async (value) => {
			if (ref.current) clearTimeout(ref.current);

			ref.current = setTimeout(async () => {
				await onAutofillShipping(value);

				resetForm();
			}, 500);
		},
		[onAutofillShipping, resetForm]
	);

	return (
		<Box flex={values.sameAsBilling ? 1 : [1, 1, 2]}>
			<Flex flexDirection={["column", "column", "row"]}>
				<Flex
					ml={["0", "0", "0.5rem"]}
					mr={!values.sameAsBilling ? ["0", "0", "0.5rem"] : ""}
					flexDirection="column"
					alignItems="flex-end"
				>
					<Heading mt="0">Billing Address</Heading>
					<Box mt="0.55rem" mb="1rem">
						<Checkbox
							disabled={isPlane}
							isLoading={isLoading}
							labelText="Shipping Address is same as Billing?"
							name="sameAsBilling"
							onClick={onBlur}
						/>
					</Box>
					<Field
						{...fieldProps}
						disabled={disableForm}
						name="billing.Name"
						labelText="Name"
					/>
					<Field
						{...fieldProps}
						disabled={disableForm}
						name="billing.Address"
						labelText="Address Line 1"
					/>
					<Field
						{...fieldProps}
						disabled={disableForm}
						name="billing.Address2"
						labelText="Address Line 2"
					/>
					<Field
						{...fieldProps}
						disabled={disableForm}
						name="billing.City"
						labelText="City"
					/>
					<Field
						{...fieldProps}
						disabled={disableForm}
						name="billing.County"
						labelText="County"
					/>

					<Field
						{...fieldProps}
						disabled={disableForm}
						name="billing.Zip"
						labelText="Postal/Zip"
					/>
					<Field
						{...fieldProps}
						disabled={disableForm}
						as="select"
						onChange={onBlur}
						name="billing.Country"
						labelText="Country"
					>
						<option value="" key="default">
							Select a Country
						</option>
						{countryOptions}
					</Field>
					{billingStateOptions.length > 0 ? (
						<Field
							{...fieldProps}
							disabled={disableForm}
							as="select"
							onChange={onBlur}
							name="billing.State"
							labelText="Province/State"
						>
							<option value="" key="default">
								Select a Province/State
							</option>
							{billingStateOptions}
						</Field>
					) : null}
				</Flex>
				{!values.sameAsBilling ? (
					<AddressWrapper ml={["0", "0", "0.5rem"]} flexDirection="column">
						<Heading mt="0">Shipping Address</Heading>

						<Field
							isLoading={isLoading}
							as="select"
							name="shippingAddressId"
							onChange={(e) => {
								const value = e.target.value;
								setFieldValue("shippingAddressId", value);

								onOptionClick(value);
							}}
							value={values.shippingAddressId}
						>
							{
								<>
									<option value="select">Select a shipping address</option>
									{shippingOptions.map((sOption) => {
										const { label, value } = sOption || {};
										return (
											<option value={value} key={value}>
												{label}
											</option>
										);
									})}
								</>
							}
						</Field>
						<Field {...fieldProps} name="shipping.Name" labelText="Name" />
						<Field
							{...fieldProps}
							name="shipping.Address"
							labelText="Address Line 1"
						/>
						<Field
							{...fieldProps}
							name="shipping.Address2"
							labelText="Address Line 2"
						/>
						<Field {...fieldProps} name="shipping.City" labelText="City" />
						<Field {...fieldProps} name="shipping.County" labelText="County" />

						<Field {...fieldProps} name="shipping.Zip" labelText="Postal/Zip" />
						<Field
							{...fieldProps}
							as="select"
							onChange={onBlur}
							name="shipping.Country"
							labelText="Country"
						>
							<option value="" key="default">
								Select a Country
							</option>
							{countryOptions}
						</Field>
						{shippingStateOptions.length > 0 ? (
							<Field
								as="select"
								{...fieldProps}
								onChange={onBlur}
								name="shipping.State"
								labelText="Province/State"
							>
								<option value="" key="default">
									Select a Province/State
								</option>
								{shippingStateOptions}
							</Field>
						) : null}
					</AddressWrapper>
				) : null}
			</Flex>
		</Box>
	);
};

export default AddressForm;
